import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CustomTypeEntityType, CustomTypeEntityTypeOptions } from "../../api/CustomTypes/CustomTypesBase";
import { CustomTypeTemplateTile } from "../../common/sidebar/tiles/CustomTypeTemplateTile";
import { StepwiseWizard } from "../../common/StepwiseWizard/StepwiseWizard";
import { CustomType, customTypeConstants, CustomTypeFilters } from "../../api/CustomTypes/CustomTypes";
import { Dataset, datasetsConstants } from "../../api/Datasets";
import {
  hierarchyConstants,
  inventoriesConstants,
  InventoryItem,
  inventoryItemsConstants,
} from "../../api/Inventories";
import { Person, personsConstants } from "../../api/Person";
import { Project, projectsConstants } from "../../api/Projects";
import { Sample, samplesConstants } from "../../api/Samples";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { CustomTypeTemplate, customTypeTemplateConstants } from "../../api/CustomTypes/CustomTypeTemplates";
import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import { Redirect, useHistory } from "react-router-dom";
import { useEntityDetail, usePostMutation } from "../../api/BaseEntityApi";
import { SessionContext } from "../../common/contexts/SessionContext";
import { CustomFieldDataType } from "../../api/CustomFields/CustomFieldsBase";
import { GenericEntityConstantsEntities } from "../../api/GenericConstants";
import { EntityConstants } from "../../api/GenericTypes";
import { Tile } from "../../common/panels/Tile/Tile";
import { getDetailLink, getIndexRoute } from "../../main/Routing";
import { Alert } from "../../common/overlays/Alert/Alert";
import { CustomTypeHierarchyViewer } from "../CustomTypes/CustomTypeRenderUtils";
import { CustomTypeTile } from "../../common/sidebar/tiles/CustomTypeTile";
import { useEntityPermissions } from "../../common/permissions/useEntityPermissions";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { NotAllowed, NotSet } from "../../common/misc/UIconstants";
import { CustomFieldTemplateTile } from "../../common/sidebar/tiles/CustomFieldTemplateTile";
import { RenderTabStoreHistoryButton } from "../../common/tables/Tabs/history/TabStoreHistory";
import { customFieldTemplateFiltersToForm } from "../CustomFieldTemplates/Table/CustomFieldTemplatesFilterBar";
import { customFieldTemplateConstants } from "../../api/CustomFields/CustomFieldTemplates";
import {
  CustomTypePreviewRenderer,
  useCustomTypeEntityTypeConstantsSwitch,
} from "../CustomTypes/generics/CustomTypePreviewRenderer";

const EntityTypeToEntityConsts: Record<CustomTypeEntityType, EntityConstants> = {
  Dataset: datasetsConstants,
  InventoryItem: inventoryItemsConstants,
  Person: personsConstants,
  Project: projectsConstants,
  Sample: samplesConstants,
};

const StepURLParam = "step";
const EntityTypeURLParam = "entity_type";
const TemplateIdURLParam = "template_id";

export const useCustomTypeTemplateManagerRoutes = () => {
  const history = useHistory();
  const { route } = useContext(SessionContext);

  const baseRoute = useMemo(() => route(getIndexRoute(customTypeTemplateConstants.frontendIndexRoute)), [route]);

  const step = useMemo(
    () => Number(new URLSearchParams(history.location.search).get(StepURLParam)) || 1,
    [history.location.search]
  );
  const targetEntityTypeURL = useMemo(
    () => new URLSearchParams(history.location.search).get(EntityTypeURLParam) as CustomTypeEntityType | undefined,
    [history.location.search]
  );
  const targetTemplateId = useMemo(
    () => Number(new URLSearchParams(history.location.search).get(TemplateIdURLParam)) || undefined,
    [history.location.search]
  );

  const getTemplateManagerRouteDetails = useCallback(
    (entityType?: CustomTypeEntityType, templateId?: number) => {
      const searchParams = new URLSearchParams();
      if (entityType) {
        searchParams.set(EntityTypeURLParam, entityType);
        searchParams.set(StepURLParam, "2");
      }
      if (templateId) {
        searchParams.set(TemplateIdURLParam, templateId.toString());
      }
      return `${baseRoute}?${searchParams.toString()}`;
    },
    [baseRoute]
  );

  const openTemplateManager = useCallback(
    (entityType?: CustomTypeEntityType, templateId?: number) => {
      history.push(getTemplateManagerRouteDetails(entityType, templateId));
    },
    [history, getTemplateManagerRouteDetails]
  );

  return { step, targetEntityTypeURL, targetTemplateId, openTemplateManager, getTemplateManagerRouteDetails };
};

export const CustomTypeTemplateManager = () => {
  const history = useHistory();
  const { route } = useContext(SessionContext);
  const { canCreate } = useEntityPermissions({ entityTypeId: "customTypes" });

  const { step, targetEntityTypeURL, targetTemplateId } = useCustomTypeTemplateManagerRoutes();
  const { data: targetTemplate } = useEntityDetail<CustomTypeTemplate>(
    customTypeTemplateConstants.resource,
    targetTemplateId ?? 0,
    undefined,
    { enabled: !!targetTemplateId }
  );

  const [newCustomType, setNewCustomType] = useState<CustomType>();
  var { mutateAsync, isLoading } = usePostMutation<
    CustomType,
    { useExisting: boolean; useExistingDependencies: boolean }
  >({
    resource: `${customTypeTemplateConstants.resource}/${targetTemplateId}/create` as any,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    if (!targetEntityTypeURL && step >= 2) {
      searchParams.set(StepURLParam, "1");
      history.replace({ search: searchParams.toString() });
    } else if (!targetTemplateId && step >= 3) {
      searchParams.set(StepURLParam, "2");
      history.replace({ search: searchParams.toString() });
    } else if (!isLoading && !newCustomType && step >= 4) {
      searchParams.set(StepURLParam, "3");
      history.replace({ search: searchParams.toString() });
    } else if (step === 1 && !!targetTemplateId) {
      searchParams.delete(TemplateIdURLParam);
      history.replace({ search: searchParams.toString() });
    }
  }, [history, isLoading, newCustomType, step, targetEntityTypeURL, targetTemplateId]);

  const create = useCallback(async () => {
    if (!canCreate) {
      showtoast("error", "No permission to create custom types.");
      console.error("No permission to create custom types.");
      return;
    }
    if (!targetTemplateId) return;
    await mutateAsync(
      { body: { useExisting: false, useExistingDependencies: true } },
      {
        onSuccess(data, variables, context) {
          data && setNewCustomType(data);
        },
      }
    ).catch((e) => console.error(e));
  }, [canCreate, mutateAsync, targetTemplateId]);

  return (
    <div style={{ minWidth: "800px", overflow: "auto" }}>
      <div>
        <StepwiseWizard
          title="Template manager"
          steps={[
            {
              label: "Select content type",
              validatefn: () => !!targetEntityTypeURL,
              component: (props) => <EntitySelection selectedEntityType={targetEntityTypeURL} />,
            },
            {
              label: "Select template",
              validatefn: () => !!targetTemplateId && !!targetTemplate,
              component: (props) => (
                <TemplateSelection selectedEntityType={targetEntityTypeURL} selectedTemplateId={targetTemplateId} />
              ),
            },
            {
              label: "Create",
              validatefn: () => !!targetTemplateId && !!targetTemplate && !!canCreate,
              component: (props) => (
                <CreateConfirmation
                  selectedEntityType={targetEntityTypeURL}
                  selectedTemplateId={targetTemplateId}
                  templateAlreadyInUse={!!targetTemplate?.customTypesFromCurrentTemplate?.length}
                />
              ),
              onNext: () => !!targetTemplateId && create(),
              nextButtonLabelOverride: "Create",
            },
            {
              label: "Adjust",
              validatefn: () => !!targetTemplateId && !!targetTemplate && !isLoading && !!newCustomType,
              component: (props) =>
                !!newCustomType ? (
                  <Redirect to={route(getDetailLink(customTypeConstants.frontendIndexRoute, newCustomType.id))} />
                ) : (
                  <Alert
                    type="info"
                    message="The custom type will be created shortly. You will be redirected once done."
                    fit
                    centered
                  />
                ),
            },
          ]}
        />
      </div>
      <div>{step === 2 && <AdditionalCustomFieldsTile customTypeTemplate={targetTemplate} />}</div>
      <div>
        {!!targetEntityTypeURL && !!targetTemplate && (
          <EntityTypeToDetailPageTemplatePreview entityType={targetEntityTypeURL} customTypeTemplate={targetTemplate} />
        )}
      </div>
    </div>
  );
};

const EntitySelectButtonWrapper = ({
  entityType,
  selectedEntityType,
}: {
  entityType: CustomTypeEntityType;
  selectedEntityType?: CustomTypeEntityType;
}) => {
  const history = useHistory();
  return (
    <div>
      <button
        key={entityType}
        className={`flex align-center justify-center gap-5 btn btn-${
          selectedEntityType === entityType ? "soft-primary" : "default"
        }`}
        style={{
          width: "200px",
          height: "50px",
          boxShadow: selectedEntityType === entityType ? undefined : "1px 1px 1px 1px var(--gray-200)",
        }}
        onClick={() => {
          const searchParams = new URLSearchParams(history.location.search);
          searchParams.set(EntityTypeURLParam, entityType);
          history.replace({ search: searchParams.toString() });
        }}
      >
        <div>
          <LucideIcon name={EntityTypeToEntityConsts[entityType].icon} />
        </div>
        {toUppercase(EntityTypeToEntityConsts[entityType].entityPlural)}
      </button>
    </div>
  );
};

const EntitySelection = ({ selectedEntityType }: { selectedEntityType?: CustomTypeEntityType }) => {
  // const { route } = useContext(SessionContext);
  const options = useMemo(() => {
    return [...CustomTypeEntityTypeOptions].sort();
  }, []);
  return (
    <div>
      <h4>Which content type should be customized?</h4>
      <div className="flex row-wrap gap-5" style={{ justifyContent: "space-between" }}>
        {options.map((entityType) => (
          <EntitySelectButtonWrapper key={entityType} entityType={entityType} selectedEntityType={selectedEntityType} />
        ))}
      </div>
      {/* <hr />
      <div className="flex row-nowrap gap-5 align-center">
        <span style={{ color: "var(--info)", fontWeight: "bold" }}>
          <LucideIcon name="info" /> Looking for template packages?
        </span>
        <Link to={route(`${getIndexRoute(customTypeTemplatePackageConstants.frontendIndexRoute)}`)}>
          Some template packages are available here
        </Link>
      </div> */}
    </div>
  );
};

const TemplateSelection = ({
  selectedEntityType,
  selectedTemplateId,
}: {
  selectedEntityType?: CustomTypeEntityType;
  selectedTemplateId?: number;
}) => {
  const history = useHistory();

  const entityConsts = useCustomTypeEntityTypeConstantsSwitch(selectedEntityType);

  const defaultFilters = useMemo(
    () => ({
      entityTypes: selectedEntityType ? [selectedEntityType] : null,
      excludeHierarchyChildren: selectedEntityType === "InventoryItem" ? true : undefined,
    }),
    [selectedEntityType]
  );

  if (!selectedEntityType) return <Alert type="light" message="No customizable content selected." fit centered />;
  return (
    <CustomTypeTemplateTile
      foldable={false}
      defaultFilters={defaultFilters}
      excludeFilters={{
        entityTypes: () => false,
        excludeHierarchyChildren: () => false,
        customTypeTemplatePackageIds: (value) => !!value,
      }}
      onRowClick={(row) => {
        const searchParams = new URLSearchParams(history.location.search);
        if (row.id === selectedTemplateId) {
          searchParams.delete(TemplateIdURLParam);
        } else {
          searchParams.set(TemplateIdURLParam, row.id.toString());
        }
        history.replace({ search: searchParams.toString() });
      }}
      title={
        entityConsts?.entityConstants.resource === "inventory_items"
          ? `${toUppercase(inventoriesConstants.entityPlural)}`
          : `${toUppercase(entityConsts?.entityConstants.entitySingular ?? "Custom type")} templates`
      }
      rowId={(row) => `Version: ${row.templateVersion ?? "N/A"}`}
      rowLabel={(row) => (
        <>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {row.entityType === "InventoryItem" ? (row.inventoryName ?? row.name) : row.name}
          </span>
          {row.entityType === "InventoryItem" && (
            <>
              {" "}
              <span
                className="label label-soft-info"
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={
                  !!row.isHierarchyRoot || !!row.rootHierarchy
                    ? `This ${entityConsts?.entityConstants.entitySingular} is part of a hierarchy`
                    : `This ${entityConsts?.entityConstants.entitySingular} is a flat list`
                }
              >
                <LucideIcon
                  name={
                    !!row.isHierarchyRoot || !!row.rootHierarchy
                      ? hierarchyConstants.hierarchyLayoutIcon
                      : hierarchyConstants.flatLayoutIcon
                  }
                />{" "}
                {!!row.isHierarchyRoot || !!row.rootHierarchy ? "Hierarchy" : "List"}
              </span>
            </>
          )}
        </>
      )}
      rowInfo={(row) => (
        <span title={(row.entityType === "InventoryItem" ? row.inventoryDescription : row.description) ?? ""}>
          {(row.entityType === "InventoryItem" ? row.inventoryDescription : row.description) ?? NotSet}
        </span>
      )}
      rowDate={(row) => null}
      // rowDate={(row) =>
      //   // <div className="flex row-nowrap align-center gap-5">
      //   //   <TableArrayRenderer values={row.customTypeTemplatePackages} arrayLength={5} separator="">
      //   //     {(value) => (
      //   //       <div className="label label-soft-default flex align-center gap-5" title={value.description}>
      //   //         <div>
      //   //           <LucideIcon name="package" />
      //   //         </div>
      //   //         {value.name}
      //   //       </div>
      //   //     )}
      //   //   </TableArrayRenderer>
      //   // </div>
      // }
      rowControls={(row) => (
        <div
          className="label label-soft-info"
          style={{ visibility: !!row.customTypesFromCurrentTemplate?.length ? "visible" : "hidden" }}
        >
          Already in use
        </div>
      )}
      rowStyle={(row) =>
        row.id === selectedTemplateId
          ? { border: "1px dashed var(--primary)", backgroundColor: "var(--primary-light)" }
          : undefined
      }
    />
  );
};

export const EntityTypeToDetailPageTemplatePreview = ({
  entityType,
  customTypeTemplate,
}: {
  entityType: CustomTypeEntityType;
  customTypeTemplate?: CustomTypeTemplate;
}) => {
  const entityConstantsDict = useCustomTypeEntityTypeConstantsSwitch(entityType);
  const entityTypeId = useEntityTypeIdConstantSwitch(entityType);

  const [hierarchyNodeTypeId, setHierarchyNodeTypeId] = useState<number>();
  const { data: hierarchyNodeType } = useEntityDetail<CustomTypeTemplate>(
    customTypeTemplateConstants.resource,
    hierarchyNodeTypeId ?? 0,
    undefined,
    { enabled: !!hierarchyNodeTypeId }
  );

  useEffect(() => {
    setHierarchyNodeTypeId(undefined);
  }, [customTypeTemplate?.id, entityType]);

  if (!entityConstantsDict || !entityTypeId) return null;

  return (
    <div style={{ padding: "15px", width: "100%", overflow: "auto" }}>
      <div
        style={{
          padding: "15px",
          backgroundColor: "var(--white)",
          border: "1px solid var(--gray-100)",
          borderRadius: "5px",
          boxShadow: "0px 2px 8px rgba(20, 22, 25, 0.07)",
          width: "100%",
          overflow: "auto",
        }}
      >
        <Tile foldable={false}>
          <Tile.Head
            title={
              <div className="flex align-center gap-5">
                Preview
                {!!customTypeTemplate?.templateVersion && (
                  <div style={{ fontSize: "10px" }} className="label label-soft-info">
                    Template version: {customTypeTemplate?.templateVersion}
                  </div>
                )}
                <div style={{ fontSize: "10px" }} className="label label-soft-default">
                  Reference links are disabled in this view
                </div>
                <div style={{ fontSize: "10px" }} className="label label-soft-default">
                  Some values have been randomly generated
                </div>
              </div>
            }
          />
          <Tile.Body>
            {!!customTypeTemplate?.isHierarchyRoot && (
              <div
                style={{
                  width: "100%",
                  borderBottom: "1px solid var(--gray-200)",
                  paddingBottom: "10px",
                  marginBottom: "12px",
                }}
              >
                <CustomTypeHierarchyViewer
                  customType={(hierarchyNodeType ?? customTypeTemplate) as CustomType}
                  isTemplate
                  onLabelClick={(d) => setHierarchyNodeTypeId(d.data.id)}
                />
              </div>
            )}
            <CustomTypePreviewRenderer
              customType={(hierarchyNodeType ?? customTypeTemplate) as CustomType}
              includeDetailPageHeader
            />
          </Tile.Body>
        </Tile>
      </div>
    </div>
  );
};

const AdditionalCustomFieldsTile = ({ customTypeTemplate }: { customTypeTemplate?: CustomTypeTemplate }) => {
  return (
    <div style={{ padding: "15px", width: "100%", overflow: "auto" }}>
      <div
        style={{
          padding: "15px",
          backgroundColor: "var(--white)",
          border: "1px solid var(--gray-100)",
          borderRadius: "5px",
          boxShadow: "0px 2px 8px rgba(20, 22, 25, 0.07)",
          width: "100%",
          overflow: "auto",
        }}
      >
        <CustomFieldTemplateTile
          defaultFilters={{ customTypeTemplateIds: !!customTypeTemplate ? [customTypeTemplate.id] : [] }}
          excludeFilters={{ customTypeTemplateIds: () => false }}
          foldable
          isFolded
          disabled={!customTypeTemplate}
          title="Custom fields included in the selected template"
          controlsFixed={({ filters }) => (
            <RenderTabStoreHistoryButton
              entityConstants={customFieldTemplateConstants}
              filters={filters}
              filtersToForm={customFieldTemplateFiltersToForm}
            />
          )}
          rowControls={(entity) => (
            <div className="flex align-center gap-5" style={{ width: "95px", justifyContent: "center" }}>
              {!!entity.customFieldsFromCurrentTemplate?.length && (
                <div className="label label-soft-info">Already in use</div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

const CreateConfirmation = ({
  selectedEntityType,
  selectedTemplateId,
  templateAlreadyInUse,
}: {
  selectedEntityType?: CustomTypeEntityType;
  selectedTemplateId?: number;
  templateAlreadyInUse?: boolean;
}) => {
  const filter: CustomTypeFilters = useMemo(
    () => ({
      customTypeTemplateIds: selectedTemplateId ? [selectedTemplateId] : [],
      entityTypes: selectedEntityType ? [selectedEntityType] : null,
      excludeHierarchyChildren: selectedEntityType === "InventoryItem" ? true : undefined,
    }),
    [selectedEntityType, selectedTemplateId]
  );

  const entityConstants = useCustomTypeEntityTypeConstantsSwitch(selectedEntityType);

  const { canCreate } = useEntityPermissions({ entityTypeId: "customTypes" });

  if (!canCreate) return NotAllowed;
  if (!selectedEntityType || !entityConstants)
    return <Alert type="light" message="No customizable content selected." fit centered />;
  if (!selectedTemplateId) return <Alert type="light" message="No template selected." fit centered />;
  return (
    <div>
      <h4>Confirm creation?</h4>

      {templateAlreadyInUse ? (
        <Alert
          type="warning"
          message="This template is already in use. Are you sure you want to proceed and create a new custom type based on this template?"
          fit
          style={{ margin: 0 }}
        />
      ) : (
        <Alert
          type="info"
          message="Proceeding will create a new custom type based on the selected template."
          fit
          style={{ margin: 0 }}
        />
      )}

      {templateAlreadyInUse && (
        <div style={{ marginTop: "15px" }}>
          <h5>Custom types based on this template:</h5>
          <CustomTypeTile
            headless
            foldable={false}
            defaultFilters={filter}
            excludeFilters={{
              customTypeTemplateIds: () => false,
              entityTypes: () => false,
              excludeHierarchyChildren: () => false,
            }}
            title={`Existing ${entityConstants.entityConstants.entitySingular} types based on selected template`}
          />
        </div>
      )}
    </div>
  );
};

const useEntityTypeIdConstantSwitch: (entityType?: CustomTypeEntityType) => GenericEntityConstantsEntities | null = (
  entityType?: CustomTypeEntityType
) => {
  switch (entityType) {
    case "Dataset":
      return "datasets";
    case "InventoryItem":
      return "inventoryItems";
    case "Person":
      return "persons";
    case "Project":
      return "projects";
    case "Sample":
      return "samples";
    default:
      return null;
  }
};

export const useEntityExample = (entityType: CustomTypeEntityType) => {
  const { session } = useContext(SessionContext);
  const entityConstants = useCustomTypeEntityTypeConstantsSwitch(entityType);
  const BaseEntityValue = {
    id: 0,
    uid: "00000000-0000-0000-0000-000000000000",
    name: `Example ${entityConstants?.entityConstants.entitySingular ?? "content"}`,
    projects: [{ id: 0, name: "Example project reference" }],
    owner: { id: session?.userId || 1, name: "Example user", uid: "00000000-0000-0000-0000-000000000000" },
    isDeleted: false,
    enteredBy: { id: session?.userId || 1, name: "Example user", uid: "00000000-0000-0000-0000-000000000000" },
    enteredOn: new Date().toISOString(),
    modifiedBy: { id: session?.userId || 1, name: "Example user", uid: "00000000-0000-0000-0000-000000000000" },
    modifiedOn: new Date().toISOString(),
    customType: { id: 0, name: "Test" },
  };

  return {
    Dataset: {
      ...BaseEntityValue,
      creationDate: new Date().toISOString(),
      path: "Example path",
      claimed: true,
      isViewableEntity: false,
      viewableEntityType: null,
      parsingState: "ParsedSuccessfully",
      parsedMetadata: { parameters: false, tracks: false, trackCount: 0, trackViewerTypes: [] },
      source: { id: 0, name: "Example source", type: "AutoUpload" },
      // legacyFields: { brukerPulseProgram: "Example pulse program", brukerTitle: "Example title" },
    } as Dataset,
    InventoryItem: {
      ...BaseEntityValue,
      isRootItem: false,
      ancestors: [],
      isHierarchyItem: false,
      parent: { id: 0, name: "" },
    } as InventoryItem,
    Person: {
      ...BaseEntityValue,
      firstName: "Max",
      lastName: "Mustermann",
      email: "max.mustermann@example.com",
      loginType: "Session",
    } as Person,
    Project: {
      ...BaseEntityValue,
    } as Project,
    Sample: {
      ...BaseEntityValue,
    } as Sample,
  };
};

export const GenerateCustomFieldValues = (customTypeTemplate: CustomTypeTemplate) => {
  const customFieldValues: Record<string, any> = {};
  customTypeTemplate.sections?.forEach((s) =>
    s.customFields?.forEach(
      (cf) => (customFieldValues[cf.id] = GetExampleValueByDataType(cf.dataType, cf.predefinedOptions))
    )
  );
  return customFieldValues;
};

const GetExampleValueByDataType = (dataType: CustomFieldDataType, predefinedOptions?: any[]) => {
  var getExampleEnumValue = () => predefinedOptions?.[Math.floor(Math.random() * predefinedOptions.length)];
  switch (dataType) {
    case "Boolean":
      return Math.random() > 0.5;
    case "Date":
      return new Date().toISOString();
    case "Dataset":
      return { id: 0, name: "Dataset reference" };
    case "Person":
      return { id: 0, name: "Person reference" };
    case "LabNotebook":
      return { id: 0, name: "Notebook reference" };
    case "LabNotebookEntry":
      return { id: 0, name: "Notebook entry reference" };
    case "LabNotebookExperiment":
      return { id: 0, name: "Notebook experiment reference" };
    case "Method":
      return { id: 0, name: "Method reference" };
    case "Project":
      return { id: 0, name: "Project reference" };
    case "Sample":
      return { id: 0, name: "Sample reference" };
    case "SharedContent":
      return { id: 0, name: "Shared content reference" };
    case "Attachment":
      return { id: 0, name: "Attachment reference" };
    case "InventoryItem":
      return { id: 0, name: "Inventory item reference" };
    case "DatasetArray":
      return [{ id: 0, name: "Dataset reference" }];
    case "PersonArray":
      return [{ id: 0, name: "Person reference" }];
    case "LabNotebookArray":
      return [{ id: 0, name: "Notebook reference" }];
    case "LabNotebookEntryArray":
      return [{ id: 0, name: "Notebook entry reference" }];
    case "LabNotebookExperimentArray":
      return [{ id: 0, name: "Notebook experiment reference" }];
    case "MethodArray":
      return [{ id: 0, name: "Method reference" }];
    case "ProjectArray":
      return [{ id: 0, name: "Project reference" }];
    case "SampleArray":
      return [{ id: 0, name: "Sample reference" }];
    case "SharedContentArray":
      return [{ id: 0, name: "Shared content reference" }];
    case "InventoryItemArray":
      return [{ id: 0, name: "Inventory item reference" }];
    case "DateArray":
      var dateArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        dateArray.push(new Date().toISOString());
      }
      return dateArray;
    case "DateTime":
      return new Date().toISOString();
    case "DateTimeArray":
      var dateTimeArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        dateTimeArray.push(new Date().toISOString());
      }
      return dateTimeArray;
    case "DateTimeRange":
      return [new Date().toISOString(), new Date().toISOString()];
    case "Barcode":
      return null;
    case "Float":
      return getExampleEnumValue() ?? 1.23;
    case "FloatArray":
      var floatArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        floatArray.push(getExampleEnumValue() ?? 1.23);
      }
      return Array.from(new Set(floatArray)).sort();
    case "Integer":
      return getExampleEnumValue() ?? 123;
    case "IntegerArray":
      var integerArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        integerArray.push(getExampleEnumValue() ?? 123);
      }
      return Array.from(new Set(integerArray)).sort();
    case "String":
      return getExampleEnumValue() ?? "Example text";
    case "StringArray":
      var stringArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        stringArray.push(getExampleEnumValue() ?? "Example text");
      }
      return Array.from(new Set(stringArray)).sort();
    case "Time":
      return new Date().toISOString();
    case "TimeArray":
      var timeArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        timeArray.push(new Date().toISOString());
      }
      return timeArray;
    case "TimeRange":
      return [new Date().toISOString(), new Date().toISOString()];
    case "Url":
      return getExampleEnumValue() ?? "https://www.google.com";
    case "UrlArray":
      var urlArray = [];
      for (let i = 0; i < Math.random() * 10; i++) {
        urlArray.push(getExampleEnumValue() ?? "https://www.google.com");
      }
      return Array.from(new Set(urlArray)).sort();
    default:
      return null;
  }
};
